import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { can, canAny, type EffectivePermissions, type MePermissionsResponse } from '../../lib/rbac/permissions';
import { useAuth } from '../auth/use-auth';
import { PermissionContext, type PermissionContextValue, type PermissionStatus } from './permission-context';

interface PermissionState {
  status: PermissionStatus;
  permissions: EffectivePermissions;
}

const EMPTY: EffectivePermissions = {};

/**
 * Lädt die effektiven Permissions des eingeloggten Benutzers vom Server.
 * Scheitert das Laden, bleibt die Menge leer: unbekannt heißt ausblenden, nie
 * „vorsichtshalber anzeigen“.
 */
export const PermissionProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [state, setState] = useState<PermissionState>({ status: 'loading', permissions: EMPTY });

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setState({ status: 'anonymous', permissions: EMPTY });
      return;
    }
    setState((previous) => ({ ...previous, status: 'loading' }));
    try {
      const response = await api.get<MePermissionsResponse>('/api/me/permissions');
      setState({ status: 'ready', permissions: response?.permissions ?? EMPTY });
    } catch {
      setState({ status: 'error', permissions: EMPTY });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authLoading) void load();
  }, [authLoading, load, user?.id]);

  const value = useMemo<PermissionContextValue>(
    () => ({
      status: authLoading ? 'loading' : state.status,
      permissions: state.permissions,
      can: (key, options) => can(state.permissions, key, options),
      canAny: (keys, options) => canAny(state.permissions, keys, options),
      refresh: load,
    }),
    [authLoading, state, load],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};
